import { useEffect, useState } from 'react';
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Radio,
  DatePicker,
  message,
  Drawer,
  Tooltip,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  broadcastList,
  broadcastUpdate,
  broadcastRevoke,
  broadcastDelete,
  type BroadcastWithStats,
  type BroadcastPriority,
} from '@/services/broadcast';
import styles from './BroadcastAdmin.module.css';

const PRIORITY_TAG: Record<BroadcastPriority, { color: string; label: string }> = {
  normal: { color: 'blue', label: '普通' },
  important: { color: 'orange', label: '重要' },
  critical: { color: 'red', label: '紧急' },
};

interface EditValues {
  title: string;
  contentMd: string;
  priority: BroadcastPriority;
  expireAt?: Dayjs | null;
}

function History() {
  const [data, setData] = useState<BroadcastWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<BroadcastWithStats | null>(null);
  const [viewing, setViewing] = useState<BroadcastWithStats | null>(null);
  const [editForm] = Form.useForm<EditValues>();

  const drawerWidth = Math.min(720, window.innerWidth - 16);
  const modalWidth = Math.min(560, window.innerWidth - 32);

  const load = async () => {
    setLoading(true);
    try {
      const list = await broadcastList();
      setData(list);
    } catch {
      // 拦截器
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRevoke = (b: BroadcastWithStats) => {
    Modal.confirm({
      title: '撤回广播？',
      content: `撤回后用户将不再收到《${b.title}》，已弹窗的内容不会自动消失。`,
      okText: '确认撤回',
      okButtonProps: { danger: true },
      onOk: async () => {
        await broadcastRevoke(b.id);
        message.success('已撤回');
        load();
      },
    });
  };

  const handleDelete = (b: BroadcastWithStats) => {
    Modal.confirm({
      title: '删除广播？',
      content: `《${b.title}》将连同收件状态一起被永久删除。`,
      okText: '永久删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        await broadcastDelete(b.id);
        message.success('已删除');
        load();
      },
    });
  };

  const openEdit = (b: BroadcastWithStats) => {
    setEditing(b);
    editForm.setFieldsValue({
      title: b.title,
      contentMd: b.contentMd,
      priority: b.priority,
      expireAt: b.expireAt ? dayjs(b.expireAt) : null,
    });
  };

  const submitEdit = async () => {
    try {
      const values = await editForm.validateFields();
      if (!editing) return;
      await broadcastUpdate(editing.id, {
        title: values.title,
        contentMd: values.contentMd,
        priority: values.priority,
        expireAt: values.expireAt ? values.expireAt.toISOString() : null,
      });
      message.success('已更新');
      setEditing(null);
      load();
    } catch {
      // ignored
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.statusBar}>
        <span className={styles.statusBarHint}>
          已发布广播 {data.length} 条
        </span>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
      </div>
      <Table<BroadcastWithStats>
        rowKey="id"
        dataSource={data}
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: '标题',
            dataIndex: 'title',
            ellipsis: true,
            render: (t, r) => (
              <a onClick={() => setViewing(r)} style={{ fontWeight: 500 }}>
                {t}
              </a>
            ),
          },
          {
            title: '优先级',
            dataIndex: 'priority',
            width: 90,
            render: (p: BroadcastPriority) => (
              <Tag color={PRIORITY_TAG[p].color}>{PRIORITY_TAG[p].label}</Tag>
            ),
          },
          {
            title: '范围',
            dataIndex: 'targetType',
            width: 110,
            render: (_t, r) =>
              r.targetType === 'all' ? (
                <Tag color="cyan">全员</Tag>
              ) : (
                <Tag>定向 {JSON.parse(r.targetUserIds || '[]').length} 人</Tag>
              ),
          },
          {
            title: '已读 / 总数',
            width: 110,
            render: (_t, r) =>
              r.targetCount === -1
                ? `${r.readCount} / 全员`
                : `${r.readCount} / ${r.targetCount}`,
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (s) =>
              s === 'revoked' ? (
                <Tag color="default">已撤回</Tag>
              ) : (
                <Tag color="green">生效中</Tag>
              ),
          },
          {
            title: '过期时间',
            dataIndex: 'expireAt',
            width: 170,
            render: (t) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '—'),
          },
          {
            title: '发布时间',
            dataIndex: 'createdAt',
            width: 170,
            render: (t) => dayjs(t).format('YYYY-MM-DD HH:mm'),
          },
          {
            title: '操作',
            width: 200,
            render: (_t, r) => (
              <Space size="small">
                <Tooltip title={r.status === 'revoked' ? '已撤回，不可编辑' : ''}>
                  <Button
                    type="link"
                    size="small"
                    disabled={r.status === 'revoked'}
                    onClick={() => openEdit(r)}
                  >
                    编辑
                  </Button>
                </Tooltip>
                {r.status === 'active' && (
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={() => handleRevoke(r)}
                  >
                    撤回
                  </Button>
                )}
                <Button
                  type="link"
                  size="small"
                  danger
                  onClick={() => handleDelete(r)}
                >
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
      />

      {/* 编辑 Modal */}
      <Modal
        title="编辑广播"
        open={!!editing}
        onOk={submitEdit}
        onCancel={() => setEditing(null)}
        okText="保存"
        cancelText="取消"
        width={modalWidth}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, max: 255 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="priority"
            label="优先级"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value="normal">普通</Radio>
              <Radio value="important">重要</Radio>
              <Radio value="critical">紧急</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="expireAt" label="过期时间">
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: 280 }} />
          </Form.Item>
          <Form.Item
            name="contentMd"
            label="内容 (Markdown)"
            rules={[{ required: true }]}
          >
            <Input.TextArea autoSize={{ minRows: 8, maxRows: 18 }} style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情 Drawer */}
      <Drawer
        open={!!viewing}
        onClose={() => setViewing(null)}
        width={drawerWidth}
        title={viewing?.title}
      >
        {viewing && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Tag color={PRIORITY_TAG[viewing.priority].color}>
                {PRIORITY_TAG[viewing.priority].label}
              </Tag>
              {viewing.targetType === 'all' ? (
                <Tag color="cyan">全员</Tag>
              ) : (
                <Tag>定向 {JSON.parse(viewing.targetUserIds || '[]').length} 人</Tag>
              )}
              <Tag color={viewing.status === 'active' ? 'green' : 'default'}>
                {viewing.status === 'active' ? '生效中' : '已撤回'}
              </Tag>
            </div>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 16 }}>
              发布于 {dayjs(viewing.createdAt).format('YYYY-MM-DD HH:mm')}
              {viewing.expireAt && (
                <> · 过期 {dayjs(viewing.expireAt).format('YYYY-MM-DD HH:mm')}</>
              )}
            </div>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {viewing.contentMd}
            </ReactMarkdown>
          </>
        )}
      </Drawer>
    </div>
  );
}

export default History;
